import { useMemo, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import './DecisionGraphCanvas.css'

export interface CanvasNode {
  key: string
  label: string
  /** Node chrome variant — DRD: `table`(default)/`gate`/`aggregate`; Calc: the six `DagExecutor` types. */
  variant: string
  /** Small muted text under the label — a citation (DRD) or an op/expression detail (Calc). */
  subtitle?: string | undefined
}

export interface CanvasEdge {
  from: string
  to: string
}

export interface DecisionGraphCanvasProps {
  ariaLabel: string
  nodes: CanvasNode[]
  edges: CanvasEdge[]
  selectedKey: string | null
  onSelect: (key: string) => void
  /** e.g. the DRD's "edges inferred from referenced decision keys" disclosure, or nothing for Calc's real edges. */
  caption?: string | undefined
  emptyMessage?: string | undefined
}

const COL_WIDTH = 190
const ROW_HEIGHT = 70
const NODE_WIDTH = 160
const NODE_HEIGHT = 46

interface Positioned extends CanvasNode {
  x: number
  y: number
}

/**
 * Shared canvas infrastructure for BOTH the Eligibility DRD (design doc §4.3, inferred edges) and
 * the Calc DAG (design doc §4.7.1, real authored edges) — design-system.md §3.5 `DAGExplorer`
 * contract: zoomable/pannable SVG, node click → selection, a parallel tree/table view ALWAYS
 * available (never canvas-only, a11y-mandatory).
 *
 * Layout: a deterministic topological-level layout (no live DOM measurement needed — every node's
 * (x, y) is computed from its level/row index), rendered as an SVG line layer plus a synced HTML
 * `<button>` overlay at the same coordinates (this file's a11y choice named in the design doc §4.3:
 * "via `foreignObject` or a synced HTML overlay"). Nodes are visited breadth-first from every
 * node with no incoming edge, so DOM/tab order is genuinely topological — a node with no inferred
 * incoming edge always precedes what depends on it.
 */
export function DecisionGraphCanvas({ ariaLabel, nodes, edges, selectedKey, onSelect, caption, emptyMessage }: DecisionGraphCanvasProps) {
  const [scale, setScale] = useState(1)
  const [showTree, setShowTree] = useState(true)

  const { positioned, width, height } = useMemo(() => layoutNodes(nodes, edges), [nodes, edges])

  if (nodes.length === 0) {
    return <div className="iw-dagcanvas__empty">{emptyMessage ?? 'No nodes yet.'}</div>
  }

  return (
    <div className="iw-dagcanvas">
      <div className="iw-dagcanvas__toolbar">
        <button type="button" className="iw-icon-btn-sm" aria-label="Zoom in" onClick={() => setScale((s) => Math.min(2, s + 0.2))}>
          <Icon name="zoom-in" size={14} />
        </button>
        <button type="button" className="iw-icon-btn-sm" aria-label="Zoom out" onClick={() => setScale((s) => Math.max(0.4, s - 0.2))}>
          <Icon name="zoom-out" size={14} />
        </button>
        <button type="button" className="iw-icon-btn-sm" aria-label="Reset zoom" onClick={() => setScale(1)}>
          <Icon name="maximize" size={14} />
        </button>
        <button
          type="button"
          className="iw-btn iw-btn--sm"
          aria-pressed={showTree}
          onClick={() => setShowTree((v) => !v)}
          style={{ marginLeft: 'auto' }}
        >
          <Icon name="list-tree" size={13} />
          {showTree ? 'Hide list view' : 'Show list view'}
        </button>
      </div>

      <div className="iw-dagcanvas__body">
        <div className="iw-dagcanvas__viewport">
          <div className="iw-dagcanvas__scaled" style={{ transform: `scale(${scale})`, width, height }}>
            <svg
              className="iw-dagcanvas__svg"
              width={width}
              height={height}
              role="img"
              aria-label={`${ariaLabel} — node diagram`}
            >
              {edges.map((edge, i) => {
                const from = positioned.find((p) => p.key === edge.from)
                const to = positioned.find((p) => p.key === edge.to)
                if (!from || !to) return null
                return (
                  <line
                    key={`${edge.from}-${edge.to}-${i}`}
                    x1={from.x + NODE_WIDTH / 2}
                    y1={from.y + NODE_HEIGHT}
                    x2={to.x + NODE_WIDTH / 2}
                    y2={to.y}
                    stroke="var(--iw-border-strong)"
                    strokeWidth={1.5}
                    markerEnd="url(#iw-dagcanvas-arrow)"
                  />
                )
              })}
              <defs>
                <marker id="iw-dagcanvas-arrow" markerWidth={8} markerHeight={8} refX={6} refY={4} orient="auto">
                  <path d="M0,0 L8,4 L0,8 Z" fill="var(--iw-border-strong)" />
                </marker>
              </defs>
            </svg>
            {positioned.map((node) => (
              <button
                key={node.key}
                type="button"
                className={nodeClassName(node, node.key === selectedKey)}
                style={{ left: node.x, top: node.y, width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
                onClick={() => onSelect(node.key)}
                aria-pressed={node.key === selectedKey}
              >
                <span className="iw-dagcanvas__node-label">{node.label}</span>
                {node.subtitle ? <span className="iw-dagcanvas__node-subtitle">{node.subtitle}</span> : null}
              </button>
            ))}
          </div>
        </div>

        {showTree ? (
          <ul className="iw-dagcanvas__tree" role="tree" aria-label={`${ariaLabel} — list view`}>
            {nodes.map((node) => (
              <li key={node.key} role="treeitem" aria-selected={node.key === selectedKey}>
                <button type="button" className="iw-dagcanvas__tree-item" onClick={() => onSelect(node.key)}>
                  <span className="iw-dagcanvas__tree-variant">{node.variant}</span>
                  {node.label}
                  {node.subtitle ? <span className="iw-dagcanvas__tree-subtitle">{node.subtitle}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {caption ? <p className="iw-dagcanvas__caption">{caption}</p> : null}
    </div>
  )
}

function nodeClassName(node: CanvasNode, selected: boolean): string {
  const classes = ['iw-dagcanvas__node', `iw-dagcanvas__node--${node.variant}`]
  if (selected) classes.push('iw-dagcanvas__node--selected')
  return classes.join(' ')
}

/** Deterministic BFS-level layout — no DOM measurement, so it is stable in both real browsers and
 * jsdom tests. Nodes with no incoming edge are level 0 (visited/tab-ordered first); a cycle (should
 * never happen for a real, validated Calc graph, but a DRD's inferred edges are best-effort and
 * could theoretically form one) falls back to level 0 for any node never reached, rather than
 * looping forever. */
function layoutNodes(nodes: CanvasNode[], edges: CanvasEdge[]): { positioned: Positioned[]; width: number; height: number } {
  const incoming = new Map<string, number>()
  for (const n of nodes) incoming.set(n.key, 0)
  for (const e of edges) {
    if (incoming.has(e.to)) incoming.set(e.to, (incoming.get(e.to) ?? 0) + 1)
  }
  const outgoing = new Map<string, string[]>()
  for (const e of edges) {
    if (!outgoing.has(e.from)) outgoing.set(e.from, [])
    outgoing.get(e.from)!.push(e.to)
  }

  const level = new Map<string, number>()
  const queue: string[] = nodes.filter((n) => (incoming.get(n.key) ?? 0) === 0).map((n) => n.key)
  for (const key of queue) level.set(key, 0)
  const visited = new Set(queue)
  while (queue.length > 0) {
    const key = queue.shift()!
    const currentLevel = level.get(key) ?? 0
    for (const next of outgoing.get(key) ?? []) {
      const candidate = currentLevel + 1
      if (!level.has(next) || candidate > level.get(next)!) level.set(next, candidate)
      if (!visited.has(next)) {
        visited.add(next)
        queue.push(next)
      }
    }
  }
  // Anything unreached (a cycle, or genuinely disconnected) still gets a level so it is never hidden.
  for (const n of nodes) if (!level.has(n.key)) level.set(n.key, 0)

  const byLevel = new Map<number, string[]>()
  for (const n of nodes) {
    const lvl = level.get(n.key)!
    if (!byLevel.has(lvl)) byLevel.set(lvl, [])
    byLevel.get(lvl)!.push(n.key)
  }

  const positioned: Positioned[] = []
  let maxLevel = 0
  let maxRows = 1
  // Ascending level order (not Map insertion order) — this is what makes DOM/tab order genuinely
  // topological: every level-0 node (no inferred incoming edge) precedes every level-1 node, etc.
  const sortedLevels = [...byLevel.keys()].sort((a, b) => a - b)
  for (const lvl of sortedLevels) {
    const keys = byLevel.get(lvl)!
    maxLevel = Math.max(maxLevel, lvl)
    maxRows = Math.max(maxRows, keys.length)
    keys.forEach((key, row) => {
      const node = nodes.find((n) => n.key === key)!
      positioned.push({ ...node, x: lvl * COL_WIDTH + 16, y: row * ROW_HEIGHT + 16 })
    })
  }

  return {
    positioned,
    width: (maxLevel + 1) * COL_WIDTH + 32,
    height: maxRows * ROW_HEIGHT + 32,
  }
}
